import React, { forwardRef, useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import './index.scss'
import { GroupedTableInfo } from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable/GroupedTable'
import GroupedPublishersFooter from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable/GroupedPublishersFooter'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import PublishersTableRows from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable/PublisherTableRows'
import MlcTooltip from '@App/kit/components/Tooltips/MlcTooltip'

interface GroupedOverclaimPublishersTableProps {
    allowShareEditing: boolean
    onValidation: (isValid: boolean) => void
}

export interface GroupedPublishers {
    [topMemberName: string]: OriginalPublisherModel[]
}

const GroupedPublishersTable = forwardRef<HTMLDivElement, GroupedOverclaimPublishersTableProps>(
    (props: GroupedOverclaimPublishersTableProps, ref) => {
        const { allowShareEditing, onValidation } = props
        const { overclaimViewStore } = useContext<RootStore>(StoresContext)
        const [groupedPublisherInfo, setGroupedPublisherInfo] = useState<GroupedTableInfo[][]>()
        const [shareCount, setShareCount] = useState<number>(0)

        useEffect(() => {}, [allowShareEditing])

        const amendShareToZeroTooltipId = 'overclaim-amend-share-zero-error'

        const updateGroupedPublisherShares = (publisherGroup: number, shareDifference: number) => {
            if (groupedPublisherInfo && publisherGroup !== -1) {
                setGroupedPublisherInfo((prevGroupedPublisherInfo) => {
                    if (!prevGroupedPublisherInfo) {
                        return
                    }
                    const newGroupedPublishers = [...prevGroupedPublisherInfo]
                    const groupedValue = newGroupedPublishers[publisherGroup]?.[0]?.groupedValue
                    if (typeof groupedValue === 'number') {
                        newGroupedPublishers[publisherGroup][0].groupedValue = groupedValue - shareDifference
                    }
                    return newGroupedPublishers
                })
            }
        }

        const updateShare = (model: OriginalPublisherModel, newValue: number, adminModel?: PublisherModel) => {
            let entityToBeUpdated: OriginalPublisherModel | PublisherModel | undefined
            const modelIpid = adminModel ? adminModel.ipId : model.ipId
            overclaimViewStore.overclaimViewItem?.originalPublishers.forEach((publisher) => {
                if (publisher.ipId === model.ipId) {
                    if (!adminModel) {
                        // if not an admin publisher we always update the original publisher
                        entityToBeUpdated = publisher
                    } else {
                        // if an admin publisher we update the admin publisher, but both the admin and op must match
                        const foundAdmin = publisher.administratorPublishers.find((admin) => admin.ipId === modelIpid)
                        if (foundAdmin) {
                            entityToBeUpdated = foundAdmin
                        }
                    }
                }
            })
            if (groupedPublisherInfo && entityToBeUpdated) {
                if (entityToBeUpdated) {
                    const shareDifference =
                        entityToBeUpdated.publisherShare != null
                            ? parseFloat((Number(entityToBeUpdated.publisherShare) - newValue).toFixed(2))
                            : 0
                    entityToBeUpdated.publisherShare = newValue

                    const publisherGroup = groupedPublisherInfo.findIndex((group) =>
                        group.some((publisher) => publisher.rows[0].name === String(modelIpid))
                    )
                    updateGroupedPublisherShares(publisherGroup, shareDifference)
                    setShareCount((prevShareCount) => prevShareCount - shareDifference)
                }
            }
        }

        return (
            <>
                <MlcTooltip
                    style={{ maxWidth: '25rem' }}
                    className="tooltip"
                    id={amendShareToZeroTooltipId}
                    variant="warning"
                    place="top"
                >
                    <span>
                        If you are trying to enter zero
                        <strong> (0) </strong>as your share amount, please return to the previous screen and select the
                        <strong> Relinquish Shares </strong>
                        button to relinquish all of your shares on this work.
                    </span>
                </MlcTooltip>
                <PublishersTableRows
                    groupedPublisherInfo={groupedPublisherInfo}
                    setGroupedPublisherInfo={setGroupedPublisherInfo}
                    allowShareEditing={allowShareEditing}
                    setShareCount={setShareCount}
                    updateShare={updateShare}
                    onValidation={onValidation}
                    ref={ref}
                />
                <GroupedPublishersFooter totalShares={shareCount} />
            </>
        )
    }
)
GroupedPublishersTable.displayName = 'GroupedPublishersTable'
export default observer(GroupedPublishersTable)
